import CancelIcon from '@material-ui/icons/Cancel'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked'
import type { FindingCategory, FindingResolutionState } from 'api/graphQL/types/specIntelligence.types'
import { ComponentType, CSSProperties } from 'react'

/** Spec Intelligence brand gradient — purple-to-blue, used on AI action buttons. */
export const SPEC_INT_GRADIENT = 'linear-gradient(135deg, #5B2D8E 0%, #1565C0 100%)'

/** Darkened hover variant of {@link SPEC_INT_GRADIENT}. */
export const SPEC_INT_GRADIENT_HOVER = 'linear-gradient(135deg, #4a2275 0%, #0d47a1 100%)'

/** Accent color for AI-generated content (AI Recs tile, AI badge). */
export const SPEC_INT_AI_COLOR = '#6A0DAD'

/** Drawer panel background, shared by the disclaimer, drawer shell, and drawer header. */
export const SPEC_INT_PANEL_BACKGROUND_COLOR = '#EDF0F6'

/** Background of the round AI icon badge in the drawer header and progress view. */
export const SPEC_INT_ICON_CIRCLE_BACKGROUND_COLOR = '#E3F2FD'

/** Icon/text color inside the round AI icon badge. */
export const SPEC_INT_ICON_CIRCLE_COLOR = '#1565C0'

/** Brand navy used for the progress title, finding "Resolve"/"Go to section" actions. */
export const SPEC_INT_NAVY_COLOR = '#04325B'

/** Neutral gray for de-emphasized text, dots, and disabled/outlined borders. */
export const SPEC_INT_NEUTRAL_GRAY_COLOR = '#9E9E9E'

/** Muted body text color, shared across drawer views and finding cards. */
export const SPEC_INT_MUTED_TEXT_COLOR = '#595959'

/** Secondary text color, shared across drawer views (titles, confirm messages, buttons). */
export const SPEC_INT_SECONDARY_TEXT_COLOR = '#314959'

/** Error state color, shared across drawer views (icon, label text, destructive button). */
export const SPEC_INT_ERROR_COLOR = '#C62828'

/** Display label for the Cross-References finding category. */
export const CROSS_REFERENCES_LABEL = 'Cross-References'

/** Display label for the ErrorsAndOmissions finding category. */
export const OMISSION_LABEL = 'Errors & Omissions'

/** Display label for the Contradictions finding category. */
export const CONTRADICTION_LABEL = 'Contradictory Language'

/** Display label for the Coordination finding category. */
export const COORDINATION_LABEL = 'Coordination'

/** Every finding category, in the order checks run/are presented. Single source of truth for the analysis footer and the category filter pills. */
export const ALL_FINDING_CATEGORIES: FindingCategory[] = ['CrossReferences', 'ErrorsAndOmissions', 'Contradictions', 'Coordination']

/** Maps each finding category to its display label. Single source of truth for both the finding-card chip and the category-selection pills. */
export const CATEGORY_DISPLAY_LABELS: Record<FindingCategory, string> = {
    CrossReferences: CROSS_REFERENCES_LABEL,
    ErrorsAndOmissions: OMISSION_LABEL,
    Contradictions: CONTRADICTION_LABEL,
    Coordination: COORDINATION_LABEL,
}

/** Maps each finding category to its chip background/text colors. Single source of truth for both the finding-card chip and the category-selection pills. */
export const CATEGORY_COLORS: Record<FindingCategory, { backgroundColor: string; color: string }> = {
    ErrorsAndOmissions: { backgroundColor: '#FFCDD2', color: '#B71C1C' },
    CrossReferences: { backgroundColor: '#FFF3E6', color: '#A84F15' },
    Contradictions: { backgroundColor: '#EDE7F6', color: '#4527A0' },
    Coordination: { backgroundColor: '#FFCC80', color: '#993B00' },
}

/** Maps each resolution state to its badge background/text colors. Single source of truth for both the finding-card state badge and the state-selection pills. */
export const STATE_COLORS: Record<FindingResolutionState, { backgroundColor: string; color: string }> = {
    Open: { backgroundColor: '#EFF6FF', color: '#1D4ED8' },
    Resolved: { backgroundColor: '#F0FDF4', color: '#166534' },
    Dismissed: { backgroundColor: '#F1F5F9', color: '#334155' },
}

/** Maps each resolution state to its icon. Single source of truth for both the finding-card state badge and the state-selection pills. */
export const STATE_ICONS: Record<FindingResolutionState, ComponentType<{ className?: string; style?: CSSProperties }>> = {
    Open: RadioButtonUncheckedIcon,
    Resolved: CheckCircleIcon,
    Dismissed: CancelIcon,
}

/** Section-selection count above which we warn the user their picker choice may exceed the AI token limit. */
export const MAX_RECOMMENDED_SECTIONS = 40

/** Warning shown in the picker when selection exceeds {@link MAX_RECOMMENDED_SECTIONS}. */
export const TOKEN_LIMIT_WARNING_MESSAGE =
    'Note: This selection may exceed the AI token limit. If you get an error, try selecting fewer sections.'
